import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, useStyleConfig} from '@chakra-ui/react'
import {HeroBannerText} from './hero-banner-text'
import {remCalc} from '../../../../../plugins/px-to-rem-babel-custom-plugin/utils/remCalc'
import {calculateVerticalPadding, getHerobannerGradient} from '../utils'
import {
    getAspectRatioObjectFromImageSet,
    getDamImagePicture,
    useEditorialVideoBaseUrl,
} from '../../../custom-hooks/use-dam-image'

export const HeroBanner = ({
    isMaskHeaderActive = true,
    textBlock,
    variant = 'white', // theme can either be black or white
    height = 'big',
    maxWidth = remCalc(1920),
    hasPaddings = true,
    buttonText,
    countdown,
    isFirstElement = false,
    ...props
}) => {
    const imageSetKey = `image_${height}`
    const imageSetObj = props?.[imageSetKey]
    const isSmallHeight = height === 'small'
    const isMediumHeight = height === 'mid'
    const styles = useStyleConfig('HeroBanner')
    const videoRef = useRef()

    const direction =
        textBlock?.tabletMobileAlignment === 'bottom-center' ? 'column' : 'column-reverse'
    const verticalPadding = calculateVerticalPadding(direction, hasPaddings)

    const isVideo = props?.videos?.desktop && props?.videos?.mobile

    const gradient = getHerobannerGradient(
        textBlock.desktopAlignment,
        props?.maskgroup?.opacity,
        isMaskHeaderActive,
    )

    const ratio = getAspectRatioObjectFromImageSet(imageSetObj)

    useEffect(() => {
        const videoElement = videoRef.current
        if (!videoElement) return

        const observerOptions = {
            root: null, // use the viewport as the reference
            threshold: 0.5,
        }

        const handleIntersection = (entries) => {
            entries.forEach(async (entry) => {
                if (entry.isIntersecting) {
                    try {
                        // Use async/await to better handle the play() Promise
                        await videoElement.play()
                    } catch (error) {
                        // Autoplay blocked by browser or interrupted by rapid scrolling
                        console.warn('Autoplay prevented:', error.message)
                    }
                }
            })
        }

        const observer = new IntersectionObserver(handleIntersection, observerOptions)
        observer.observe(videoElement)

        // Cleanup: use disconnect to completely shut down the observer
        return () => {
            observer.disconnect()
        }
    }, [])

    const videoBaseUrl = useEditorialVideoBaseUrl()

    return (
        <Flex
            data-testid={`hero-banner-container-${direction}`}
            data-cs-label="editorial-herobanner"
            flexDir={direction}
            pb={verticalPadding}
            maxW={maxWidth}
            backgroundColor={
                variant === 'black' ? 'background.base.inverse' : 'background.base.default'
            }
            {...styles.heroBannerContainer}
        >
            <Flex
                data-testid={
                    isVideo
                        ? 'hero-banner-video'
                        : imageSetObj
                        ? 'hero-banner-image'
                        : 'hero-banner-text'
                }
                maxW={maxWidth}
                {...styles.heroBannerMain}
            >
                {!isVideo && imageSetObj && (
                    <Box position="relative" width="100%">
                        {getDamImagePicture(
                            imageSetObj,
                            textBlock?.title || 'hero-banner-image',
                            isFirstElement,
                            ratio,
                            {objectFit: 'cover'},
                            {base: 'none', lg: gradient},
                        )}
                    </Box>
                )}

                {isVideo && (
                    <>
                        <Box
                            aspectRatio={
                                Object.keys(ratio).length > 0 ? ratio : {base: 1.172, md: 3}
                            }
                            {...styles.heroBannerVideo}
                            _after={{
                                background: {lg: gradient ? `${gradient}` : undefined},
                            }}
                        >
                            <video
                                playsInline
                                ref={videoRef}
                                autoPlay={videoRef.current?.started ?? isFirstElement}
                                muted
                                loop
                                aria-hidden="true"
                                style={{
                                    height: '100%',
                                    width: '100%',
                                    objectFit: 'cover',
                                }}
                                preload={isFirstElement ? 'auto' : 'metadata'}
                            >
                                <source
                                    src={videoBaseUrl + props?.videos?.mobile}
                                    type="video/mp4"
                                    media="(max-width: 1023px)"
                                />
                                <source
                                    src={videoBaseUrl + props?.videos?.desktop}
                                    type="video/mp4"
                                    media="(min-width: 1024px)"
                                />
                                Your browser does not support the video tag.
                            </video>
                        </Box>
                    </>
                )}
            </Flex>
            <Box {...styles.heroBannerUnified}>
                <HeroBannerText
                    variant={variant}
                    maxWidth={maxWidth}
                    countdown={countdown}
                    textBlock={textBlock}
                    hasPaddings={hasPaddings}
                    buttonText={buttonText}
                    isSmallHeight={isSmallHeight}
                    isMediumHeight={isMediumHeight}
                    isFirstElement={isFirstElement}
                />
            </Box>
        </Flex>
    )
}

HeroBanner.propTypes = {
    isMaskHeaderActive: PropTypes.bool,
    textBlock: PropTypes.shape({
        label: PropTypes.string,
        title: PropTypes.string,
        bodyCopy: PropTypes.string,
        desktopAlignment: PropTypes.string,
        tabletMobileAlignment: PropTypes.string,
    }),
    buttonText: PropTypes.object,
    variant: PropTypes.string,
    height: PropTypes.string,
    link: PropTypes.string,
    maxWidth: PropTypes.string,
    hasPaddings: PropTypes.bool,
    countdown: PropTypes.array,
    maskgroup: PropTypes.shape({
        opacity: PropTypes.string,
    }),
    videos: PropTypes.shape({
        desktop: PropTypes.string,
        mobile: PropTypes.string,
    }),
    isFirstElement: PropTypes.bool,
}
